import { useTheme as useMeridianTheme } from '@amzn/meridian/theme';
import { css, keyframes } from '@emotion/css';
import { CatalogJobStatus } from '../api/services';
import { reducedMotionQuery } from '../theme/motion';

const pulseKeyframes = keyframes({
  '0%': { boxShadow: '0 0 0 0 currentColor', opacity: 1 },
  '70%': { boxShadow: '0 0 0 5px transparent', opacity: 0.7 },
  '100%': { boxShadow: '0 0 0 0 transparent', opacity: 1 },
});

const supportTokenSuffixByStatus = {
  [CatalogJobStatus.SUCCESS]: 'Success',
  [CatalogJobStatus.PENDING]: 'Warning',
  [CatalogJobStatus.FAILURE]: 'Error',
} as const;

const defaultLabelByStatus = {
  [CatalogJobStatus.SUCCESS]: 'Success',
  [CatalogJobStatus.PENDING]: 'Pending',
  [CatalogJobStatus.FAILURE]: 'Failed',
} as const;

interface StatusBadgeProps {
  status: CatalogJobStatus;
  pendingLabel?: string;
}

export default function StatusBadge({ status, pendingLabel }: StatusBadgeProps) {
  const meridianTheme = useMeridianTheme();
  const tokenSuffix = supportTokenSuffixByStatus[status];
  const label = status === CatalogJobStatus.PENDING && pendingLabel ? pendingLabel : defaultLabelByStatus[status];
  const inkColor = meridianTheme(`themeSupportInk${tokenSuffix}`);

  const badgeClass = css({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    height: 24,
    padding: '0 10px',
    boxSizing: 'border-box',
    borderRadius: 999,
    border: `1px solid ${meridianTheme(`themeSupportBorder${tokenSuffix}`)}`,
    backgroundColor: meridianTheme(`themeSupportFill${tokenSuffix}`),
    color: inkColor,
    fontSize: 12,
    fontWeight: 600,
    lineHeight: 1,
    whiteSpace: 'nowrap',
  });
  const dotClass = css({
    width: 6,
    height: 6,
    flex: '0 0 auto',
    borderRadius: '50%',
    backgroundColor: 'currentColor',
    animation: status === CatalogJobStatus.PENDING ? `${pulseKeyframes} 1.6s ease-out infinite` : undefined,
    [reducedMotionQuery]: { animation: 'none' },
  });

  return (
    <span className={badgeClass}>
      <span className={dotClass} aria-hidden="true" />
      {label}
    </span>
  );
}
